import React, { useMemo, useState } from 'react';
import {
  Database, Plus, X, Search, GitBranch, CornerDownRight, Info,
} from 'lucide-react';
import { Input } from '@/components/ui/input';

/**
 * DataSourcePicker — the guided "primary + related objects" selector
 * (HubSpot-style). Shared by CreateReportModal (step 2) and the builder's
 * Data Sources dialog.
 *
 * Props:
 *   graph      {objects: [...], relations: {objKey: [rel...]}} from
 *              metadataApi.getReportGraph()
 *   primary    object summary {key,label,plural} — fixed here; picking
 *              the primary happens upstream.
 *   related    current relation list [{key,label,plural,via,viaLabel,
 *              direction,anchor,join}]
 *   onChange   (nextRelated) => void
 *   maxRelated number (default 3)
 *
 * Every relation carries:
 *   anchor  the object key it attaches to (primary key or a previously
 *           added related object's key) — enables chains like
 *           Account → Contact → Opportunity.
 *   join    'with' (inner — only rows that have a match) or
 *           'with_or_without' (left — keep unmatched rows).
 */

export const MAX_RELATED_OBJECTS = 3;

const JOIN_WITH = 'with';
const JOIN_WITH_OR_WITHOUT = 'with_or_without';

// Human phrasing for a relation row / candidate.
const directionPhrase = (rel, anchorLabel) => (
  rel.direction === 'parent'
    ? `${anchorLabel} links up to one ${rel.label}`
    : `${anchorLabel} can have many ${rel.plural || rel.label}`
);

export const summarySentence = (primary, related) => {
  if (!primary) return '';
  let s = `Returns ${primary.plural || primary.label}`;
  (related || []).forEach((r) => {
    const joinWord = r.join === JOIN_WITH ? 'with' : 'with or without';
    const anchorSuffix = r.anchor && r.anchor !== primary.key ? ` (from ${r.anchorLabel || r.anchor})` : '';
    s += `, ${joinWord} ${r.direction === 'parent' ? 'their' : 'related'} ${r.plural || r.label}${anchorSuffix}`;
  });
  return `${s}.`;
};

const JoinToggle = ({ value, onChange }) => (
  <div className="flex rounded-md overflow-hidden border border-slate-200 flex-shrink-0" title='"With" keeps only records that have a match. "With or without" keeps all records.'>
    {[[JOIN_WITH, 'With'], [JOIN_WITH_OR_WITHOUT, 'With or without']].map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`px-2 py-1 text-[10px] font-semibold whitespace-nowrap transition-colors ${
          value === v ? 'bg-indigo-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
        }`}>
        {label}
      </button>
    ))}
  </div>
);

const DataSourcePicker = ({ graph, primary, related, onChange, maxRelated = MAX_RELATED_OBJECTS }) => {
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');

  const relations = graph?.relations || {};
  const selectedKeys = useMemo(
    () => new Set([primary?.key, ...(related || []).map((r) => r.key)]),
    [primary, related],
  );

  // Candidate relations: hops available from the primary AND from every
  // already-selected related object (anchored chains), excluding objects
  // already in the report. Deduped by (anchor, target, via).
  const candidates = useMemo(() => {
    if (!primary) return [];
    const anchors = [
      { key: primary.key, label: primary.label },
      ...(related || []).map((r) => ({ key: r.key, label: r.label })),
    ];
    const seen = new Set();
    const out = [];
    anchors.forEach((anchor) => {
      (relations[anchor.key] || []).forEach((rel) => {
        if (selectedKeys.has(rel.key)) return;
        const sig = `${anchor.key}|${rel.direction}|${rel.key}|${rel.via}`;
        if (seen.has(sig)) return;
        seen.add(sig);
        out.push({ ...rel, anchor: anchor.key, anchorLabel: anchor.label });
      });
    });
    const q = search.trim().toLowerCase();
    const filtered = q
      ? out.filter((c) => `${c.label} ${c.plural} ${c.viaLabel} ${c.anchorLabel}`.toLowerCase().includes(q))
      : out;
    // Primary-anchored hops first, then alphabetical.
    return filtered.sort((a, b) => (
      (a.anchor === primary.key ? 0 : 1) - (b.anchor === primary.key ? 0 : 1)
      || (a.label || '').localeCompare(b.label || '')
    ));
  }, [primary, related, relations, selectedKeys, search]);

  const addRelation = (cand) => {
    const entry = {
      key: cand.key,
      label: cand.label,
      plural: cand.plural,
      via: cand.via,
      viaLabel: cand.viaLabel,
      direction: cand.direction,
      anchor: cand.anchor,
      anchorLabel: cand.anchorLabel,
      join: JOIN_WITH_OR_WITHOUT,
    };
    onChange([...(related || []), entry]);
    setAdding(false);
    setSearch('');
  };

  const removeRelation = (idx) => {
    // Removing a source also removes everything anchored on it,
    // TRANSITIVELY — a chain Primary → B → C → D must lose C and D when
    // B goes, otherwise D's dangling anchor silently blanks (or, with an
    // inner join, zeroes) the report.
    const dead = new Set([related[idx].key]);
    let next = related.filter((_, i) => i !== idx);
    let changed = true;
    while (changed) {
      changed = false;
      next = next.filter((r) => {
        if (r.anchor && dead.has(r.anchor)) {
          dead.add(r.key);
          changed = true;
          return false;
        }
        return true;
      });
    }
    onChange(next);
  };

  const setJoin = (idx, join) => {
    onChange(related.map((r, i) => (i === idx ? { ...r, join } : r)));
  };

  const atCap = (related || []).length >= maxRelated;

  return (
    <div className="space-y-3">
      {/* Primary card */}
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-indigo-600 flex-shrink-0">
          <Database className="h-4 w-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-indigo-500">Primary object</div>
          <div className="text-sm font-semibold text-slate-900 truncate">{primary?.label}</div>
        </div>
        <span className="text-[11px] text-slate-500 flex-shrink-0">Every row starts from a {primary?.label}</span>
      </div>

      {/* Related chain */}
      {(related || []).map((rel, idx) => (
        <div key={`${rel.anchor}-${rel.key}-${rel.via}`} className="flex items-start gap-2 ml-4">
          <CornerDownRight className="h-4 w-4 text-slate-300 mt-3.5 flex-shrink-0" />
          <div className="flex-1 rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100 flex-shrink-0">
                <GitBranch className="h-4 w-4 text-slate-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{rel.plural || rel.label}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {directionPhrase(rel, rel.anchorLabel || rel.anchor || primary?.label)}
                  <span className="text-slate-400"> · relationship: {rel.viaLabel || rel.via}</span>
                </div>
              </div>
              <JoinToggle value={rel.join || JOIN_WITH_OR_WITHOUT} onChange={(v) => setJoin(idx, v)} />
              <button type="button" onClick={() => removeRelation(idx)}
                className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex-shrink-0"
                aria-label={`Remove ${rel.label}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Add related */}
      {!adding ? (
        <div className="ml-4 flex items-center gap-2">
          <button
            type="button"
            disabled={atCap}
            onClick={() => setAdding(true)}
            className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-dashed transition-colors ${
              atCap
                ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                : 'border-indigo-300 text-indigo-600 hover:bg-indigo-50'
            }`}
          >
            <Plus className="h-4 w-4" /> Add related object
          </button>
          {atCap && (
            <span className="text-[11px] text-slate-400">
              Maximum of {maxRelated} related objects per report
            </span>
          )}
        </div>
      ) : (
        <div className="ml-4 rounded-xl border border-indigo-200 bg-white shadow-sm">
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input autoFocus placeholder="Search related objects…" value={search}
                onChange={(e) => setSearch(e.target.value)} className="pl-8 h-8 text-sm" />
            </div>
            <button type="button" onClick={() => { setAdding(false); setSearch(''); }}
              className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-56 overflow-y-auto p-1.5">
            {candidates.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-slate-400">
                {search ? 'No related objects match your search.' : 'No more related objects are available from the current selection.'}
              </div>
            )}
            {candidates.map((c) => (
              <button
                key={`${c.anchor}-${c.key}-${c.via}`}
                type="button"
                onClick={() => addRelation(c)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-indigo-50 transition-colors"
              >
                <div className="p-1.5 rounded-lg bg-slate-100 flex-shrink-0">
                  <Database className="h-3.5 w-3.5 text-slate-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {c.plural || c.label}
                    {c.isCustom && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Custom</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {directionPhrase(c, c.anchorLabel)}
                    {(c.ambiguous || c.anchor !== primary?.key) && (
                      <span className="text-slate-400"> · via {c.viaLabel || c.via}</span>
                    )}
                  </div>
                </div>
                <Plus className="h-4 w-4 text-indigo-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Live summary */}
      <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
        <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-slate-600 leading-relaxed">{summarySentence(primary, related)}</p>
      </div>
    </div>
  );
};

export default DataSourcePicker;
